import React, { useState, useRef } from 'react';
import { FiCamera, FiX } from 'react-icons/fi';

interface ImageUploaderProps {
  onImageUpload: (file: File) => Promise<void>;
  isUploading: boolean;
}

export const ImageUploader: React.FC<ImageUploaderProps> = ({ onImageUpload, isUploading }) => {
  const [uploadedImage, setUploadedImage] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        await onImageUpload(file);
        setUploadedImage(file);
      } catch (error) {
        console.error('Error uploading image:', error);
      }
    }
  };

  const handleRemoveImage = () => {
    setUploadedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="mt-2">
      <div
        className={`relative border border-gray-300 rounded-md ${
          uploadedImage ? 'bg-gray-50' : 'hover:border-gray-400 transition-colors'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
          id="image-upload"
        />
        {!uploadedImage ? (
          <label htmlFor="image-upload" className="cursor-pointer block text-center py-4">
            <FiCamera className="mx-auto h-6 w-6 text-gray-400 mb-1" />
            <span className="block text-sm text-gray-600">Add a photo of your meal</span>
            <span className="text-xs text-gray-400">(Optional)</span>
          </label>
        ) : (
          <div className="relative">
            <img
              src={URL.createObjectURL(uploadedImage)}
              alt="Meal preview"
              className="w-full h-36 object-cover rounded-md"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}
      </div>
      {isUploading && (
        <div className="mt-2 flex items-center justify-center text-indigo-600">
          <svg
            className="animate-spin h-5 w-5 mr-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="text-sm">Uploading image...</span>
        </div>
      )}
    </div>
  );
};
